"use client";

import { useQuery } from "@tanstack/react-query";
import { useToken } from "@trydig/centra/client";
import { Suspense, useState } from "react";
import { rpc } from "#rpc";
import { CartListingCard } from "@/components/ui/CartListingCard";
import type { RecommendedProduct } from "@/components/ui/sections/RecommendationsCarousel";
import { Typography } from "@/components/ui/Typography";
import { QuickAddSlot } from "@/features/cart/QuickAddSlot";
import { useTranslations } from "@/i18n/client";
import { productCanonicalPath } from "@/i18n/routing";
import { useRecommendations } from "@/lib/depict/useRecommendations";
import { useRecentlyViewedIds } from "@/lib/hooks/useRecentlyViewed";

const RECENTLY_VIEWED_LIMIT = 8;
// Rows, not a carousel — cap what we actually render so the empty-cart state
// doesn't turn into a scroll of everything ever viewed.
const MAX_DISPLAYED = 5;

/**
 * Renders "recently viewed" products as a row list in the empty cart state —
 * image | name + price | inline quick-add, one row per product, capped at
 * `MAX_DISPLAYED`.
 *
 * Prefers Depict's `recently_viewed` recommendations (session-based: `DepictView`
 * hands Depict a `session_id` via `dpc.setSessionId` plus the variant id of
 * every PDP visited via `dpc.setProductId`, so Depict can look that history up
 * itself — we don't seed this call with `productIds`, just the same session
 * id). Depict's pipeline takes several minutes to index a page view though,
 * so while it's catching up (or for a visitor with no Depict session at all)
 * we fall back to `localStorage`-tracked ids (`RecentlyViewedTracker`, written
 * on every PDP visit) rehydrated into full product cards via
 * `products.exploration`. The two aren't merged — Depict wins whenever it has
 * anything, since it's the richer, personalized signal.
 *
 * Quietly hides itself if neither source has anything — an empty cart should
 * still feel finished without it.
 */
export const CartRecentlyViewed = () => {
	return (
		<Suspense fallback={null}>
			<CartRecentlyViewedInner />
		</Suspense>
	);
};

const CartRecentlyViewedInner = () => {
	const t = useTranslations();
	const token = useToken();

	const { data, isLoading: depictLoading } = useRecommendations({
		productIds: [],
		type: "recently_viewed",
		limit: RECENTLY_VIEWED_LIMIT,
		enabled: Boolean(token),
		sessionId: token ?? undefined,
	});

	const depictProducts = (data?.products ?? []) as RecommendedProduct[];

	// Only reach for the fallback once we know Depict isn't going to deliver
	// this render — `isLoading` (not `isPending`) so a tokenless visitor, for
	// whom the Depict query never fires at all, doesn't block on it forever.
	const needsFallback = !depictLoading && depictProducts.length === 0;
	const localIds = useRecentlyViewedIds();

	const { data: localData } = useQuery({
		queryKey: ["recently-viewed-fallback", localIds],
		// `relatedProducts: true` — QuickAddPanel's colour swatches read from
		// `product.variants`, and without this those come back with no `items`
		// (blank swatch, no sizes, can't be added). See `products.route.ts`.
		queryFn: () => rpc.products.exploration({ ids: localIds, relatedProducts: true }),
		enabled: needsFallback && localIds.length > 0,
		staleTime: 60 * 1000,
	});

	// `products.exploration` returns cards in the same order as `localIds`
	// (most-recent-first), so no re-sorting needed here. These never carry
	// `depictProductId`/`depictListingResultId` — they weren't served by
	// Depict, so DPC shouldn't attribute them as if they were.
	const localProducts = (localData?.products ?? []) as RecommendedProduct[];

	const products = (depictProducts.length > 0 ? depictProducts : localProducts).slice(
		0,
		MAX_DISPLAYED,
	);

	const [quickAddId, setQuickAddId] = useState<string | null>(null);
	const quickAddProduct = quickAddId ? products.find((p) => p.id === quickAddId) : undefined;

	// Skip the skeleton state entirely — see `CartUpsell` for why: both sources
	// may legitimately return nothing, and flashing a skeleton row only to
	// vanish a moment later reads as a bug.
	if (products.length === 0) return null;

	return (
		<section
			aria-label={t("Recently viewed")}
			className="flex w-full flex-col gap-grid-component-spacing-md"
		>
			<Typography variant="body-sm-reg" as="h2" className="text-text-dark-text-1">
				{t("Recently viewed")}
			</Typography>
			<div className="flex flex-col gap-grid-component-spacing-md">
				{products.map((product) => {
					const isActive = product.id === quickAddId;
					return (
						<CartListingCard
							key={product.id}
							type="addon-small"
							image={{
								src: product.media?.[0]?.href ?? "",
								alt: product.media?.[0]?.alt || product.name || "",
							}}
							name={product.name ?? ""}
							href={product.uri ? productCanonicalPath(product.uri) : undefined}
							price={product.price}
							addButtonLabel={isActive ? undefined : t("Add")}
							onAdd={isActive ? undefined : () => setQuickAddId(product.id ?? null)}
						/>
					);
				})}
			</div>
			<QuickAddSlot product={quickAddProduct} onClose={() => setQuickAddId(null)} />
		</section>
	);
};
